import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

const READINESS_DOT_BG = {
  'Hot Lead':    'bg-orange-500',
  'Warm Lead':   'bg-yellow-500',
  'Active':      'bg-[#00D4FF]',
  'Early Stage': 'bg-gray-500',
  'Cold':        'bg-slate-600',
};

const PIPELINE_SEGMENTS = [
  { key: 'hot_lead',    label: 'Hot Lead',    bg: 'bg-orange-500',  dot: 'bg-orange-500' },
  { key: 'warm_lead',   label: 'Warm Lead',   bg: 'bg-yellow-500',  dot: 'bg-yellow-500' },
  { key: 'active',      label: 'Active',      bg: 'bg-[#00D4FF]',   dot: 'bg-[#00D4FF]' },
  { key: 'early_stage', label: 'Early Stage', bg: 'bg-gray-500',    dot: 'bg-gray-500' },
  { key: 'cold',        label: 'Cold',        bg: 'bg-slate-600',   dot: 'bg-slate-600' },
];

function relativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString.replace(' ', 'T'));
  if (isNaN(then.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return then.toLocaleDateString();
}

function readinessLabelFor(score) {
  if (score >= 80) return 'Hot Lead';
  if (score >= 60) return 'Warm Lead';
  if (score >= 40) return 'Active';
  if (score >= 20) return 'Early Stage';
  return 'Cold';
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5">
      <p className="text-gray-400 text-xs md:text-sm">{label}</p>
      <p className="text-white text-2xl md:text-3xl font-bold mt-1 leading-none">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

export default function BrokerDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentSummaries, setAgentSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [sumRes, agentsRes, actRes] = await Promise.all([
        authFetch(`${API}/api/broker-dashboard/summary`),
        authFetch(`${API}/api/broker-dashboard/agents`),
        authFetch(`${API}/api/broker-dashboard/recent-activity`),
      ]);
      if (sumRes.ok)    setSummary(await sumRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
      if (actRes.ok)    setAgentSummaries(await actRes.json());
    } catch (err) {
      console.error('Broker dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    setInsightsError('');
    try {
      const res = await authFetch(`${API}/api/insights/daily/broker`);
      const data = await res.json();
      if (!res.ok) {
        setInsightsError(data.detail || 'Could not generate insights');
      } else {
        setInsights(data);
        setInsightsExpanded(true);
      }
    } catch (err) {
      setInsightsError('Network error');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePrintInsights = () => {
    if (!insights || !insights.actions || insights.actions.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const today = new Date().toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    const brokerName = escapeHtml(user?.full_name || 'Broker');

    const cards = insights.actions.map(a => {
      const p = Math.max(1, Math.min(5, parseInt(a.priority, 10) || 5));
      const agent = a.agent_name
        ? `<span class="agent-name">${escapeHtml(a.agent_name)}</span> `
        : '';
      const reasoning = a.reasoning
        ? `<p class="reasoning">${escapeHtml(a.reasoning)}</p>`
        : '';
      return `
        <div class="action-card priority-${p}">
          <div class="priority-num">${p}</div>
          <div class="action-content">
            <p class="action-text">${agent}${escapeHtml(a.action)}</p>
            ${reasoning}
          </div>
        </div>`;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Broker Action Plan — ${brokerName}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      max-width: 800px; margin: 0 auto; padding: 40px; color: #1a1a1a; background: #ffffff; line-height: 1.5; }
    .header { border-bottom: 2px solid #00BCD4; padding-bottom: 16px; margin-bottom: 24px; }
    .header .label { font-size: 11px; color: #888; letter-spacing: 2px; text-transform: uppercase; }
    .header h1 { font-size: 24px; margin: 6px 0 4px 0; }
    .header .date { color: #555; font-size: 14px; }
    .action-card { display: flex; align-items: flex-start; gap: 14px; border-left: 4px solid #9CA3AF;
      background: #fafafa; padding: 14px 18px; margin-bottom: 12px; border-radius: 0 6px 6px 0; page-break-inside: avoid; }
    .action-card.priority-1 { border-left-color: #00C853; background: #f0fbf3; }
    .action-card.priority-2, .action-card.priority-3 { border-left-color: #0097A7; background: #f0fafc; }
    .action-card.priority-4, .action-card.priority-5 { border-left-color: #9CA3AF; background: #fafafa; }
    .priority-num { flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%;
      font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center;
      border: 1px solid #ccc; background: #ffffff; }
    .priority-1 .priority-num { color: #00833D; border-color: #00C853; }
    .priority-2 .priority-num, .priority-3 .priority-num { color: #006978; border-color: #0097A7; }
    .priority-4 .priority-num, .priority-5 .priority-num { color: #555; border-color: #9CA3AF; }
    .action-content { flex: 1; min-width: 0; }
    .action-text { margin: 0 0 6px 0; font-size: 14px; color: #1a1a1a; }
    .agent-name { font-weight: 600; color: #0097A7; }
    .reasoning { margin: 0; font-size: 12px; color: #555; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }
    @media print {
      body { padding: 24px; }
      .action-card, .priority-num { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="label">SchemeSync RE — Broker Action Plan</div>
    <h1>Broker Action Plan for ${brokerName}</h1>
    <div class="date">${escapeHtml(today)}</div>
  </div>
  ${cards}
  <div class="footer">Generated by SchemeSync RE — re.aida-advisory.com</div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.print();
  };

  // Role guard
  if (user && user.role !== 'broker' && user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg font-semibold mb-2">Access Denied</div>
          <p className="text-gray-400 text-sm">This dashboard is for brokers and super admins only.</p>
          <Link to="/" className="text-[#00D4FF] text-sm mt-4 inline-block hover:text-[#00E676]">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading dashboard...</div>
      </div>
    );
  }

  const buckets = summary?.buyers_by_readiness || { hot_lead: 0, warm_lead: 0, active: 0, early_stage: 0, cold: 0 };
  const totalForPipeline = Object.values(buckets).reduce((a, b) => a + b, 0);
  const pct = (n) => totalForPipeline > 0 ? (n / totalForPipeline) * 100 : 0;


  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <h1 className="text-xl md:text-2xl font-bold text-white mb-1">Broker Dashboard</h1>
        <p className="text-[#B0BEC5] mb-6">Aggregate view across all your agents</p>

        {/* AI Insights — What Should I Do Today? */}
        <div className="mb-8">
          {!insights && !insightsLoading && !insightsError && (
            <button
              onClick={loadInsights}
              className="w-full md:w-auto px-5 py-3 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#00D4FF]/20"
            >
              ✨ What Should I Do Today?
            </button>
          )}

          {insightsLoading && (
            <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="text-xl">✨</span>
                <div>
                  <div className="text-[#00D4FF] text-sm font-medium">Analyzing your office...</div>
                  <div className="text-gray-500 text-xs mt-0.5">Reviewing agent activity, buyer pipeline, and inventory.</div>
                </div>
              </div>
            </div>
          )}

          {insightsError && !insightsLoading && (
            <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm flex justify-between items-center gap-3 flex-wrap">
              <span>{insightsError}</span>
              <button onClick={loadInsights} className="text-red-200 underline hover:text-white text-xs">Retry</button>
            </div>
          )}

          {insights && !insightsLoading && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                {insights.actions.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setInsightsExpanded(v => !v)}
                    className="flex items-center gap-2 text-white font-semibold hover:text-[#00D4FF] transition-colors"
                    aria-expanded={insightsExpanded}
                  >
                    <span>✨</span>
                    <span>
                      Broker Action Plan
                      {!insightsExpanded && ` (${insights.actions.length} action${insights.actions.length !== 1 ? 's' : ''})`}
                    </span>
                    <span className={`inline-block text-xs transition-transform duration-200 ${insightsExpanded ? 'rotate-90' : ''}`}>▶</span>
                  </button>
                ) : (
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <span>✨</span>
                    <span>Broker Action Plan</span>
                  </h2>
                )}
                <div className="flex items-center gap-4">
                  {insights.actions && insights.actions.length > 0 && (
                    <button onClick={handlePrintInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Print</button>
                  )}
                  <button onClick={loadInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Refresh</button>
                </div>
              </div>
              {insights.actions.length === 0 ? (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 text-gray-400 text-sm">
                  No specific actions to recommend right now.
                </div>
              ) : (
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${insightsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="space-y-3">
                    {insights.actions.map((a, i) => {
                      const borderClass =
                        a.priority === 1 ? 'border-l-[#00E676]' :
                        a.priority <= 3  ? 'border-l-[#00D4FF]' :
                                           'border-l-gray-500';
                      const numClass =
                        a.priority === 1 ? 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40' :
                        a.priority <= 3  ? 'bg-[#00D4FF]/20 text-[#00D4FF] border-[#00D4FF]/40' :
                                           'bg-gray-500/20 text-gray-400 border-gray-500/40';
                      return (
                        <div key={i} className={`bg-[#1A2744] rounded-r-xl border-l-4 ${borderClass} border-y border-r border-[#546E7A]/30 p-4`}>
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 w-7 h-7 rounded-full border ${numClass} flex items-center justify-center text-xs font-bold`}>
                              {a.priority}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm leading-snug">
                                {a.agent_name && (
                                  <span className="text-[#00D4FF] font-medium mr-1.5">{a.agent_name}</span>
                                )}
                                <span>{a.action}</span>
                              </p>
                              {a.reasoning && (
                                <p className="text-gray-500 text-xs mt-1.5">{a.reasoning}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <StatCard label="Total Agents" value={summary?.total_agents ?? 0} />
          <StatCard label="Total Buyers" value={summary?.total_buyers ?? 0} />
          <StatCard label="Total Properties" value={summary?.total_properties ?? 0} />
          <StatCard label="AI Reports" value={summary?.total_reports_generated ?? 0} sub="generated" />
          <StatCard
            label="Invites"
            value={`${summary?.total_invites_redeemed ?? 0} / ${summary?.total_invites_sent ?? 0}`}
            sub="redeemed / sent"
          />
        </div>

        {/* Readiness Pipeline */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
            <div>
              <h2 className="text-white font-semibold">Readiness Pipeline</h2>
              <p className="text-gray-500 text-xs mt-0.5">Where your buyers sit across the readiness spectrum</p>
            </div>
            <div className="text-left md:text-right">
              <div className="text-gray-500 text-xs">Avg readiness</div>
              <div className="text-2xl md:text-3xl font-bold text-[#00D4FF] leading-none">{summary?.avg_readiness_score ?? 0}<span className="text-base text-gray-500 font-normal"> / 100</span></div>
            </div>
          </div>

          {totalForPipeline === 0 ? (
            <div className="text-gray-500 text-sm py-4 text-center">No buyers yet.</div>
          ) : (
            <>
              <div className="w-full h-8 bg-[#0A0E27] rounded-lg overflow-hidden flex">
                {PIPELINE_SEGMENTS.map(seg => {
                  const n = buckets[seg.key] || 0;
                  if (n === 0) return null;
                  return (
                    <div
                      key={seg.key}
                      style={{ width: `${pct(n)}%` }}
                      className={`${seg.bg} h-full flex items-center justify-center text-xs font-bold text-[#0A0E27]`}
                      title={`${n} ${seg.label}`}
                    >
                      {pct(n) > 8 ? n : ''}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                {PIPELINE_SEGMENTS.map(seg => (
                  <div key={seg.key} className="flex items-center gap-2 text-xs">
                    <span className={`w-3 h-3 rounded-full ${seg.dot}`}></span>
                    <span className="text-gray-300">{seg.label}</span>
                    <span className="text-gray-500">({buckets[seg.key] || 0})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Agent Performance */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 mb-6">
          <h2 className="text-white font-semibold mb-3">Agent Performance</h2>

          {agents.length === 0 ? (
            <div className="text-gray-500 text-sm py-4 text-center">No agents in this brokerage yet.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#546E7A]/30">
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5]">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5]">Agent</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Buyers</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Properties</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Active</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Avg Readiness</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-[#B0BEC5]">Top Buyer</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-[#B0BEC5]">Invites</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a, i) => {
                      const lbl = readinessLabelFor(a.avg_readiness_score);
                      return (
                        <tr key={a.agent_id}
                            className="border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors">
                          <td className="px-3 py-3 text-gray-500 text-sm">{i + 1}</td>
                          <td className="px-3 py-3 text-white text-sm font-medium">{a.agent_name}</td>
                          <td className="px-3 py-3 text-center text-gray-300 text-sm">{a.buyer_count}</td>
                          <td className="px-3 py-3 text-center text-gray-300 text-sm">{a.property_count}</td>
                          <td className="px-3 py-3 text-center text-sm">
                            <span className={a.active_buyers > 0 ? 'text-[#00E676]' : 'text-gray-500'}>{a.active_buyers}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${READINESS_DOT_BG[lbl] || 'bg-gray-500'}`}></span>
                              <span className="text-white font-bold text-sm">{a.avg_readiness_score || 0}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm">
                            {a.top_buyer ? (
                              <Link to={`/match-board?buyer=${a.top_buyer.buyer_id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[#00D4FF] hover:underline">
                                {a.top_buyer.buyer_name} <span className="text-gray-500 text-xs">({a.top_buyer.score})</span>
                              </Link>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-300 text-sm">
                            {a.invites_redeemed}/{a.invites_sent}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {agents.map((a, i) => {
                  const lbl = readinessLabelFor(a.avg_readiness_score);
                  return (
                    <div key={a.agent_id}
                         className="rounded-lg border bg-[#0A0E27]/40 border-[#546E7A]/20 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">#{i + 1}</span>
                            <span className="text-white text-sm font-medium truncate">{a.agent_name}</span>
                          </div>
                          {a.top_buyer && (
                            <Link to={`/match-board?buyer=${a.top_buyer.buyer_id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[#00D4FF] text-xs hover:underline block mt-1 truncate">
                              Top: {a.top_buyer.buyer_name} ({a.top_buyer.score})
                            </Link>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-center gap-1.5 justify-end">
                            <span className={`w-2 h-2 rounded-full ${READINESS_DOT_BG[lbl] || 'bg-gray-500'}`}></span>
                            <span className="text-white font-bold text-sm">{a.avg_readiness_score || 0}</span>
                          </div>
                          <div className="text-gray-500 text-xs">avg readiness</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-[#546E7A]/20 text-center">
                        <div>
                          <div className="text-white text-sm font-medium">{a.buyer_count}</div>
                          <div className="text-gray-500 text-xs">Buyers</div>
                        </div>
                        <div>
                          <div className="text-white text-sm font-medium">{a.property_count}</div>
                          <div className="text-gray-500 text-xs">Listings</div>
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${a.active_buyers > 0 ? 'text-[#00E676]' : 'text-gray-500'}`}>{a.active_buyers}</div>
                          <div className="text-gray-500 text-xs">Active</div>
                        </div>
                        <div>
                          <div className="text-white text-sm font-medium">{a.invites_redeemed}/{a.invites_sent}</div>
                          <div className="text-gray-500 text-xs">Invites</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Agent Activity This Week */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
          <h2 className="text-white font-semibold mb-3">Agent Activity This Week</h2>

          {agentSummaries.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm">No agents in this brokerage yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {agentSummaries.map(s => {
                const inactive = s.total === 0;
                return (
                  <div key={s.agent_id}
                       className={`rounded-lg p-3 md:p-4 border ${
                         inactive
                           ? 'bg-yellow-900/10 border-yellow-700/30'
                           : 'bg-[#0A0E27]/40 border-[#546E7A]/20'
                       }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white text-sm font-semibold truncate">{s.agent_name}</h3>
                        <p className={`text-xs mt-1 leading-snug ${inactive ? 'text-yellow-300/80' : 'text-gray-300'}`}>
                          {inactive ? '⚠ No activity in 7 days' : s.summary}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-gray-500 text-xs">Last active</div>
                        <div className={`text-xs ${inactive ? 'text-yellow-300/70' : 'text-gray-300'}`}>
                          {s.last_activity_date ? relativeTime(s.last_activity_date) : 'Never'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
